<template>
    <app-layout>
        <template #icon> <HeaderIcon /> </template>
        <template #title> {{ $t("event_types.title") }} </template>
        <template #description> {{ $t("event_types.description") }} </template>
        <template #actions>
            <EventTypeGridModal
                :appearances="appearances"
                :event-types="eventTypes"
                :event-type-group="eventTypeGroup"
            >
                <SpecialButton variant="outline" size="icon">
                    <LayoutGrid class="w-5"></LayoutGrid>
                </SpecialButton>
            </EventTypeGridModal>

            <CreateEventTypeModal
                v-model="modalOpened"
                :integrations="integrations"
            >
                <AddButton
                    :paid-only="eventTypes.length !== 0"
                    feature="unlimited_events"
                    data-cy="create-new-event-type"
                    @click="modalOpened = true"
                >
                    {{ $t("event_types.new_event") }}</AddButton
                >
            </CreateEventTypeModal>
        </template>
        <div class="px-4 pt-4 sm:px-6 sm:pt-8">
            <div v-if="hasMultipleTeams" class="flex w-full justify-end">
                <select
                    v-model="selectedTeamId"
                    class="focus-visible:ring-iron-950 dark:bg-iron-950 dark:ring-offset-iron-950 flex h-10 w-48 rounded-lg border border-iron-200 bg-white px-3 py-2 text-sm ring-offset-white file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-iron-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:border-iron-800 dark:placeholder:text-iron-400 dark:focus-visible:ring-iron-300"
                >
                    <option :value="undefined">
                        {{ $t("event_types.all_teams") }}
                    </option>
                    <option
                        v-for="team in teams"
                        :key="team.id"
                        :value="team.id"
                    >
                        {{ getTeamName(team) }}
                    </option>
                </select>
            </div>

            <template v-if="eventTypes.length > 0">
                <div :class="[hasMultipleTeams ? '-mt-9' : '']">
                    <div v-for="(team, i) in teamsWithEventTypes" :key="i">
                        <h2
                            v-if="hasMultipleTeams"
                            class="max-w-[150px] truncate text-lg font-medium text-[#18181B]"
                        >
                            {{ getTeamName(team) }}
                        </h2>

                        <div v-if="team.eventTypes.length === 0">
                            <p class="mb-8 mt-10 text-sm text-iron-500">
                                {{ $t("event_types.team_has_no_event_type") }}
                            </p>
                        </div>
                        <div v-else class="mt-8">
                            <ZaptimeEvenTypePreview
                                v-for="eventType in team.eventTypes"
                                :key="eventType.id"
                                :event-type="eventType"
                            ></ZaptimeEvenTypePreview>
                        </div>
                    </div>
                </div>
            </template>

            <template v-else>
                <EmptyStateWithIcon
                    :title="$t('event_types.empty_state.title')"
                    :description="$t('event_types.empty_state.description')"
                >
                    <template #icon>
                        <svg
                            width="76"
                            height="76"
                            viewBox="0 0 76 76"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M22.0219 14.1338L24.2397 24.5674M42.8891 9.6984L45.1068 20.132M62.5842 38.227L58.1488 17.3598C57.8547 15.9762 57.023 14.7662 55.8367 13.9958C54.6505 13.2254 53.2067 12.9578 51.8231 13.2519L15.3056 21.0139C13.9221 21.308 12.712 22.1397 11.9416 23.326C11.1712 24.5123 10.9036 25.956 11.1977 27.3396L18.9597 63.8571C19.2538 65.2407 20.0855 66.4507 21.2718 67.2211C22.4581 67.9915 23.9018 68.2591 25.2854 67.965L46.1525 63.5296M13.4154 37.7732L60.3665 27.7934M52.8689 56.6495L59.1945 60.7574L67.4104 48.1061"
                                stroke="url(#paint0_linear_620_2321)"
                                stroke-width="4"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                            <defs>
                                <linearGradient
                                    id="paint0_linear_620_2321"
                                    x1="15.4711"
                                    y1="20.98"
                                    x2="64.0716"
                                    y2="53.3919"
                                    gradientUnits="userSpaceOnUse"
                                >
                                    <stop stop-color="#FF9B47" />
                                    <stop
                                        offset="0.147634"
                                        stop-color="#FF7447"
                                    />
                                    <stop
                                        offset="0.454086"
                                        stop-color="#FF4247"
                                    />
                                    <stop
                                        offset="0.636926"
                                        stop-color="#E92A5B"
                                    />
                                    <stop
                                        offset="0.740367"
                                        stop-color="#DD1C67"
                                    />
                                    <stop
                                        offset="0.842991"
                                        stop-color="#D31071"
                                    />
                                    <stop offset="1" stop-color="#CF0283" />
                                </linearGradient>
                            </defs>
                        </svg>
                    </template>
                </EmptyStateWithIcon>
            </template>
        </div>
    </app-layout>
</template>

<script lang="ts" setup>
import { computed, ref } from "vue";
import AppLayout from "../../../../Layouts/AppLayout.vue";
import CreateEventTypeModal from "./components/CreateEventTypeModal.vue";
import ZaptimeEvenTypePreview from "./EventTypePreview.vue";
import AddButton from "../Atomic/AddButton.vue";
import { Button as SpecialButton } from "@/components/ui/button";
import { LayoutGrid } from "lucide-vue-next";
import EventTypeGridModal from "./grid/ConfigModal.vue";
import { usePage } from "@inertiajs/vue3";
import { IAppearance } from "@/types/IAppearance";
import HeaderIcon from "./components/HeaderIcon.vue";
import type { EventType } from "./types/EventType";
import type { Team } from "../Teams/types/Team";
import { useI18n } from "vue-i18n";
import { EmptyStateWithIcon } from "@/components/empty-state-with-icon";

interface TeamWithEventTypes extends Team {
    eventTypes: EventType[];
}

export type EventTypeGroup = {
    appearance_id: number;
    avatar: string;
    created_at: string;
    default: boolean;
    description: string;
    id: number;
    team_id: number;
    title: string;
    token: string;
    updated_at: string;
    user_id: number;
    uuid: string;
    event_types: EventType[];
};

const props = defineProps<{
    integrations: any[];
    eventTypes: EventType[];
    eventTypeGroup: EventTypeGroup;
    appearances: IAppearance[];
    locationIntegrations: {
        google: boolean;
        zoom: boolean;
    };
}>();

const page = usePage();
const { t } = useI18n();

const modalOpened = ref(false);

const selectedTeamId = ref<number>();

function getTeamName(team: Team) {
    const pageProps = usePage().props;

    // @ts-expect-error
    if (pageProps.personalTeam.id === team.id) {
        return t("event_types.personal");
    }

    return team.name;
}

const teamsWithEventTypes = computed((): TeamWithEventTypes[] => {
    const eventTypes = props.eventTypes;

    const allTeamsWithEventTypes = teams.value.map((team) => {
        return {
            ...team,
            eventTypes: eventTypes.filter(
                (eventType) => eventType.team_id === team.id,
            ),
        };
    });

    if (selectedTeamId.value === undefined) {
        return allTeamsWithEventTypes;
    }

    const onlySelectedTeamOrAll = allTeamsWithEventTypes.filter((team) => {
        return team.id === selectedTeamId.value;
    });

    return onlySelectedTeamOrAll;
});

const hasMultipleTeams = computed(() => {
    return teams.value.length > 1;
});

const teams = computed(() => {
    return page.props.allTeams as Team[];
});
</script>
